<script setup>
import { computed, watch } from 'vue';
import { AGENT_CONVERSATION_PERMISSIONS } from 'dashboard/constants/permissions';
import {
  getPermissionDisabledState,
  stripSupersededPermissions,
} from 'dashboard/helper/conversationPermissionHelper';

const props = defineProps({
  permissions: {
    type: Array,
    default: () => AGENT_CONVERSATION_PERMISSIONS,
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
  participatingExclusive: {
    type: Boolean,
    default: false,
  },
});

const conversationPermissions = defineModel({
  type: Array,
  default: () => ['conversation_manage'],
});

const permissionOptions = computed(() => ({
  participatingExclusive: props.participatingExclusive,
}));

const permissionStates = computed(() =>
  props.permissions.map(permission => {
    const disabledState = getPermissionDisabledState(
      conversationPermissions.value,
      permission,
      permissionOptions.value
    );

    return {
      permission,
      blockedBy: disabledState?.blockedBy ?? null,
      disabledReason: disabledState?.reason ?? null,
    };
  })
);

watch(
  conversationPermissions,
  newValue => {
    const stripped = stripSupersededPermissions(
      newValue,
      permissionOptions.value
    );
    if (stripped.length !== newValue.length) {
      conversationPermissions.value = stripped;
    }
  },
  { deep: true }
);
</script>

<template>
  <div class="w-full py-2">
    <template v-if="showHeader">
      <label class="text-sm font-medium text-n-slate-12">
        {{ $t('AGENT_MGMT.EDIT.FORM.CONVERSATION_PERMISSIONS.LABEL') }}
      </label>
      <p class="pb-2 text-sm text-n-slate-11">
        {{ $t('AGENT_MGMT.EDIT.FORM.CONVERSATION_PERMISSIONS.NOTE') }}
      </p>
    </template>
    <div class="flex flex-col gap-2.5">
      <div
        v-for="{ permission, blockedBy, disabledReason } in permissionStates"
        :key="permission"
        class="flex flex-col"
      >
        <div
          class="flex items-center"
          :class="{
            'opacity-50 cursor-not-allowed': blockedBy,
          }"
        >
          <input
            :id="`agent-permission-${permission}`"
            v-model="conversationPermissions"
            type="checkbox"
            :value="permission"
            :disabled="!!blockedBy"
            class="ltr:mr-2 rtl:ml-2"
          />
          <label
            :for="`agent-permission-${permission}`"
            class="text-sm font-normal"
            :class="{ 'text-n-slate-9': blockedBy }"
          >
            {{ $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`) }}
          </label>
        </div>
        <p v-if="blockedBy" class="text-xs text-n-slate-9 pl-6">
          {{
            $t(
              disabledReason === 'exclusive'
                ? 'AGENT_MGMT.EDIT.FORM.CONVERSATION_PERMISSIONS.DISABLED_EXCLUSIVE'
                : 'AGENT_MGMT.EDIT.FORM.CONVERSATION_PERMISSIONS.SUPERSEDED_BY',
              {
                permission: $t(
                  `CUSTOM_ROLE.PERMISSIONS.${blockedBy.toUpperCase()}`
                ),
              }
            )
          }}
        </p>
      </div>
    </div>
  </div>
</template>
